import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
    PRIZE_AMOUNT,
    TICKET_PRICE,
    TICKET_STATUS,
    TOTAL_NUMBERS,
} from '../../../core/constants/lottery';
import { SESSION_KEYS } from '../../../core/constants/storage';
import { buildPresoldSet } from '../../../core/lottery/inventory';
import { readSession, writeSession } from '../../../core/storage/session-store';
import { useDraws } from './draws';

// Inventory + owned tickets. Mounts INSIDE <DrawsProvider>: a ticket is bound
// to a draw slot, and its win/lose state is derived from that slot's result.

const TicketsContext = createContext(null);

function newPaymentReference() {
    return crypto.randomUUID().slice(0, 8).toUpperCase();
}

export function TicketsProvider({ children }) {
    const { seed, currentSlot, upcomingSlot, resultOf } = useDraws();

    const [tickets, setTickets] = useState(
        () => readSession(SESSION_KEYS.LOTTERY_TICKETS, []) ?? [],
    );

    // Rebuilt from the seed rather than persisted — 5.500 entries of JSON is
    // ~35 KB of sessionStorage for something a PRNG reproduces exactly.
    const presold = useMemo(() => buildPresoldSet(seed), [seed]);

    useEffect(() => {
        writeSession(SESSION_KEYS.LOTTERY_TICKETS, tickets);
    }, [tickets]);

    const ownedNumbers = useMemo(
        () => new Set(tickets.map(ticket => Number(ticket.number))),
        [tickets],
    );

    const isOwned = useCallback((value) => ownedNumbers.has(value), [ownedNumbers]);
    const isPresold = useCallback((value) => presold.has(value), [presold]);
    const isTaken = useCallback(
        (value) => presold.has(value) || ownedNumbers.has(value),
        [presold, ownedNumbers],
    );

    // TSS vite/03 §"Derived state" — win/lose is computed from the slot result
    // on every render, never written back into the ticket.
    const resolvedTickets = useMemo(() => tickets.map((ticket) => {
        if (ticket.slotIndex > currentSlot) {
            return { ...ticket, status: TICKET_STATUS.PENDING, result: null, prize: 0 };
        }
        const result = resultOf(ticket.slotIndex);
        const hasWon = ticket.number === result.number;
        return {
            ...ticket,
            status: hasWon ? TICKET_STATUS.WON : TICKET_STATUS.LOST,
            result,
            prize: hasWon ? PRIZE_AMOUNT : 0,
        };
    }), [tickets, currentSlot, resultOf]);

    const stats = useMemo(() => ({
        total:   resolvedTickets.length,
        pending: resolvedTickets.filter(t => t.status === TICKET_STATUS.PENDING).length,
        won:     resolvedTickets.filter(t => t.status === TICKET_STATUS.WON).length,
        spent:   resolvedTickets.reduce((sum, t) => sum + t.price, 0),
        winnings: resolvedTickets.reduce((sum, t) => sum + t.prize, 0),
    }), [resolvedTickets]);

    const purchase = useCallback((numbers) => {
        const paymentReference = newPaymentReference();
        const purchasedAt = Date.now();
        const created = numbers.map(number => ({
            id: crypto.randomUUID(),
            number,
            slotIndex: upcomingSlot,
            price: TICKET_PRICE,
            purchasedAt,
            paymentReference,
        }));
        setTickets(current => [...created, ...current]);
        return { paymentReference, tickets: created };
    }, [upcomingSlot]);

    const reset = useCallback(() => setTickets([]), []);

    const value = useMemo(() => ({
        tickets: resolvedTickets,
        stats,
        presoldCount: presold.size,
        ownedCount: ownedNumbers.size,
        availableCount: TOTAL_NUMBERS - presold.size - ownedNumbers.size,
        isPresold,
        isOwned,
        isTaken,
        purchase,
        reset,
    }), [resolvedTickets, stats, presold.size, ownedNumbers.size, isPresold, isOwned, isTaken, purchase, reset]);

    return <TicketsContext.Provider value={value}>{children}</TicketsContext.Provider>;
}

export function useTickets() {
    const ctx = useContext(TicketsContext);
    if (!ctx) throw new Error('useTickets must be used inside <TicketsProvider>');
    return ctx;
}
